import axios from 'axios';
import { UserProfile } from '../types';

export const API_BASE_URL = '/api';

export const normalizeUserProfile = (data: any): UserProfile => {
  if (!data) return {} as UserProfile;
  const user = data.user || data;

  const idVal = user.id || user._id || user.uid || '';
  const roleStr = user.role;
  const rolesArr = Array.isArray(user.roles) ? user.roles : (roleStr ? [roleStr] : ['user']);
  const primaryRole: 'user' | 'admin' = (roleStr === 'admin' || rolesArr.includes('admin')) ? 'admin' : 'user';

  const rawKey = user.geminiApiKey || user.apiKey || '';
  const hasGeminiKey = user.hasGeminiKey ?? (rawKey.trim().length > 0);
  let geminiApiKeyMasked = user.geminiApiKeyMasked || '';
  if (hasGeminiKey && !geminiApiKeyMasked && rawKey) {
    const trimmed = rawKey.trim();
    if (trimmed.length > 8) {
      geminiApiKeyMasked = `${trimmed.slice(0, 4)}.....${trimmed.slice(-4)}`;
    } else {
      geminiApiKeyMasked = '••••••••';
    }
  }

  return {
    ...user,
    id: idVal,
    _id: user._id || idVal,
    uid: user.uid || idVal,
    displayName: user.displayName || user.fullName || user.email?.split('@')[0] || 'Người dùng',
    fullName: user.fullName || user.displayName || '',
    email: user.email || '',
    photoURL: user.photoURL || null,
    coverPhotoURL: user.coverPhotoURL || null,
    bio: user.bio || user.description || '',
    description: user.description || user.bio || '',
    apiKey: user.apiKey || '',
    geminiApiKey: user.geminiApiKey || user.apiKey || '',
    geminiApiKeyMasked,
    hasGeminiKey,
    credits: typeof user.credits === 'number' ? user.credits : (Number(user.credits) || 0),
    gradeLevel: user.gradeLevel || '',
    workplace: user.workplace || '',
    province: user.province || '',
    role: primaryRole,
    roles: rolesArr,
    emailVerified: user.emailVerified ?? true,
    favorites: Array.isArray(user.favorites) ? user.favorites : [],
    communityStats: user.communityStats ? {
      rankTitle: user.communityStats.rankTitle || 'Thành viên',
      followerCount: Number(user.communityStats.followerCount) || 0,
      followingCount: Number(user.communityStats.followingCount) || 0,
      postCount: Number(user.communityStats.postCount) || 0,
      weeklyPoints: Number(user.communityStats.weeklyPoints) || 0,
      monthlyPoints: Number(user.communityStats.monthlyPoints) || 0,
      reputationPoints: Number(user.communityStats.reputationPoints) || 0,
    } : undefined,
    vipTier: typeof user.vipTier === 'number' ? user.vipTier : (user.vipTier ? Number(user.vipTier) : 0),
    vipExpireAt: user.vipExpireAt || user.vip_expire_at || user.vipExpiredAt || null,
    createdAt: user.createdAt || user.created_at || undefined,
    updatedAt: user.updatedAt || user.updated_at || undefined
  };
};

// 1. Lớp Giao tiếp Mạng (Axios Client)
export const getStoredToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('token') || 
         localStorage.getItem('nha_giao_so_sso_token') || 
         localStorage.getItem('auth_token') || 
         localStorage.getItem('sso_token') || 
         sessionStorage.getItem('token') || 
         null;
};

export const setStoredToken = (token: string | null): void => {
  if (typeof window === 'undefined') return;
  if (token) {
    try {
      localStorage.setItem('token', token);
      localStorage.setItem('nha_giao_so_sso_token', token);
    } catch (e) {}
  } else {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('nha_giao_so_sso_token');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('sso_token');
    } catch (e) {}
  }
};

export const getAuthHeaders = (): Record<string, string> => {
  const token = getStoredToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

export const customFetch = async (input: RequestInfo | URL, init?: RequestInit) => {
  const token = getStoredToken();
  const headers = new Headers(init?.headers || {});
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  const newInit: RequestInit = { 
    ...init, 
    headers,
    credentials: 'include' as RequestCredentials 
  };
  const res = await fetch(input, newInit);
  if (res.status === 401) {
    const url = typeof input === 'string' ? input : input.toString();
    if (url.includes('/sso/exchange') || url.includes('/auth/me')) {
      if (typeof window !== 'undefined') {
        const evt = new CustomEvent('auth_unauthorized');
        window.dispatchEvent(evt);
      }
    }
  }
  return res;
};

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Bắt buộc bật để gửi HttpOnly Cookie
  headers: {
    'Content-Type': 'application/json'
  }
});

// Gắn Token tự động vào Header của mọi request
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token && config.headers) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Thiết lập Interceptor thông minh
apiClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      const url = error.config.url || '';
      // Chỉ thực hiện văng đăng nhập nếu API bị lỗi chính là API xác thực
      if (url.includes('/sso/exchange') || url.includes('/auth/me')) {
        // Có thể dispatch event báo lỗi xác thực để đá văng ra login
        if (typeof window !== 'undefined') {
          const evt = new CustomEvent('auth_unauthorized');
          window.dispatchEvent(evt);
        }
      }
      // Các API cấu hình khác bị 401 chỉ được trả về Promise.reject
    }
    return Promise.reject(error);
  }
);

export const loginApi = async (email: string, password: string) => {
  try {
    const res = await apiClient.post(`/sso/sync`, { email, password });
    if (res.data.requireVerification) {
      return {
        requireVerification: true,
        email: res.data.email || email,
        message: res.data.message || 'Vui lòng xác thực email trước khi đăng nhập.'
      };
    }
    if (res.data?.token) {
      setStoredToken(res.data.token);
    }
    return {
      ...res.data,
      user: res.data.user ? normalizeUserProfile(res.data.user) : undefined
    };
  } catch (err: any) {
    if (err.response?.data?.requireVerification || err.response?.status === 403) {
      return {
        requireVerification: true,
        email: err.response?.data?.email || email,
        message: err.response?.data?.message || 'Vui lòng xác thực email trước khi đăng nhập.'
      };
    }
    throw new Error(err.response?.data?.message || err.response?.data?.error || 'Đăng nhập thất bại.');
  }
};

export const registerApi = async (email: string, password: string, displayName?: string) => {
  try {
    const res = await apiClient.post(`/auth/register`, { email, password, displayName: displayName || email.split('@')[0] });
    if (res.data.requireVerification) {
      return {
        requireVerification: true,
        email: res.data.email || email,
        message: res.data.message || 'Mã xác thực OTP đã được gửi đến email.'
      };
    }
    if (res.data?.token) {
      setStoredToken(res.data.token);
    }
    return {
      ...res.data,
      user: res.data.user ? normalizeUserProfile(res.data.user) : undefined
    };
  } catch (err: any) {
    if (err.response?.data?.requireVerification) {
      return {
        requireVerification: true,
        email: err.response?.data?.email || email,
        message: err.response?.data?.message || 'Mã xác thực OTP đã được gửi đến email.'
      };
    }
    throw new Error(err.response?.data?.message || err.response?.data?.error || 'Đăng ký thất bại.');
  }
};

export const verifyEmailApi = async (email: string, otp: string) => {
  try {
    const res = await apiClient.post(`/auth/verify-email`, { email, otp });
    if (res.data?.token) {
      setStoredToken(res.data.token);
    }
    return {
      ...res.data,
      user: res.data.user ? normalizeUserProfile(res.data.user) : undefined
    };
  } catch (err: any) {
    throw new Error(err.response?.data?.message || err.response?.data?.error || 'Mã OTP không chính xác hoặc đã hết hạn.');
  }
};

export const resendVerificationApi = async (email: string) => {
  try {
    const res = await apiClient.post(`/auth/resend-verification`, { email });
    return res.data;
  } catch (err: any) {
    throw new Error(err.response?.data?.message || err.response?.data?.error || 'Không thể gửi lại mã xác thực.');
  }
};

export const googleLoginApi = async (idToken: string) => {
  try {
    const res = await apiClient.post(`/auth/google`, { idToken });
    if (res.data?.token) {
      setStoredToken(res.data.token);
    }
    return {
      ...res.data,
      user: res.data.user ? normalizeUserProfile(res.data.user) : undefined
    };
  } catch (err: any) {
    throw new Error(err.response?.data?.message || err.response?.data?.error || 'Đăng nhập bằng Google thất bại.');
  }
};

// Dữ liệu user gọi duy nhất từ endpoint GET /api/sso/exchange
export const verifySsoTokenApi = async (): Promise<{ valid: boolean; user?: UserProfile }> => {
  try {
    const res = await apiClient.get(`/sso/exchange`);
    if (res.data?.token) {
      setStoredToken(res.data.token);
    }
    return { valid: true, user: normalizeUserProfile(res.data.user || res.data) };
  } catch (err) {
    return { valid: false };
  }
};

export const getUserProfileApi = async (): Promise<{ success: boolean; user?: UserProfile }> => {
  try {
    const res = await apiClient.get(`/sso/exchange`);
    if (res.data?.token) {
      setStoredToken(res.data.token);
    }
    return { success: true, user: normalizeUserProfile(res.data.user || res.data) };
  } catch (err) {
    return { success: false };
  }
};

export const updateUserProfileApi = async (formValues: any): Promise<{ success: boolean; message?: string; user?: UserProfile }> => {
  try {
    const res = await apiClient.put(`/users/profile`, formValues);
    return {
      success: true,
      message: res.data.message || 'Đã cập nhật thông tin hồ sơ thành công!',
      user: normalizeUserProfile(res.data.user || res.data.data?.user || res.data)
    };
  } catch (err: any) {
    return {
      success: false,
      message: err.response?.data?.message || err.message || 'Lỗi kết nối máy chủ khi cập nhật hồ sơ.'
    };
  }
};

export const logoutApi = async () => {
  try {
    await apiClient.post(`/sso/logout`);
  } catch (err) {
    console.warn("Logout API call error:", err);
  }
};

export const changePasswordApi = async (currentPassword: string, newPassword: string) => {
  try {
    const res = await apiClient.post(`/auth/change-password`, { currentPassword, newPassword });
    return res.data;
  } catch (err: any) {
    throw new Error(err.response?.data?.message || err.response?.data?.error || 'Đổi mật khẩu thất bại.');
  }
};

export const forgotPasswordApi = async (email: string) => {
  try {
    const res = await apiClient.post(`/auth/forgot-password`, { email });
    return res.data;
  } catch (err: any) {
    throw new Error(err.response?.data?.message || err.response?.data?.error || 'Yêu cầu đặt lại mật khẩu thất bại.');
  }
};

export const resetPasswordApi = async (token: string, newPassword: string) => {
  try {
    const res = await apiClient.post(`/auth/reset-password`, { token, newPassword });
    return res.data;
  } catch (err: any) {
    throw new Error(err.response?.data?.message || err.response?.data?.error || 'Liên kết đã hết hạn hoặc không hợp lệ, vui lòng thử lại.');
  }
};

// ======================= USER CRUD APIS ======================= //

export const getUsersApi = async (): Promise<UserProfile[]> => {
  const res = await apiClient.get(`/users`);
  const rawList = Array.isArray(res.data) ? res.data : (res.data.users || []);
  return rawList.map(normalizeUserProfile);
};

export const getUserByIdApi = async (id: string): Promise<UserProfile> => {
  const res = await apiClient.get(`/users/${id}`);
  return normalizeUserProfile(res.data.user || res.data);
};

export const createUserApi = async (userData: Partial<UserProfile>): Promise<UserProfile> => {
  const res = await apiClient.post(`/users`, userData);
  return normalizeUserProfile(res.data.user || res.data);
};

export const updateUserApi = async (id: string, userData: Partial<UserProfile>): Promise<UserProfile> => {
  const res = await apiClient.put(`/users/${id}`, userData);
  return normalizeUserProfile(res.data.user || res.data);
};

export const deleteUserApi = async (id: string): Promise<boolean> => {
  await apiClient.delete(`/users/${id}`);
  return true;
};

export const updateUserRoleApi = async (id: string, role: 'user' | 'admin'): Promise<UserProfile> => {
  const res = await apiClient.patch(`/users/${id}/role`, { role });
  return normalizeUserProfile(res.data.user || res.data);
};

export const saveGeminiApiKeyApi = async (apiKey: string): Promise<{ success: boolean; message: string; user: UserProfile; geminiApiKeyMasked: string }> => {
  const res = await apiClient.post(`/user/gemini-key`, { apiKey });
  return {
    success: true,
    message: res.data.message || 'Đã mã hóa và lưu Gemini API Key thành công!',
    geminiApiKeyMasked: res.data.geminiApiKeyMasked,
    user: normalizeUserProfile(res.data.user)
  };
};

export const getMediaListApi = async (
  folder: string = 'class/teacher_avatar'
): Promise<{ success: boolean; data?: any[]; files?: any[]; urls: string[]; message?: string }> => {
  try {
    const res = await apiClient.get(`/v1/media/list?folder=${encodeURIComponent(folder)}`);
    const data = res.data;
    const rawList = Array.isArray(data) ? data : (Array.isArray(data.data) ? data.data : (Array.isArray(data.items) ? data.items : (Array.isArray(data.files) ? data.files : (Array.isArray(data.urls) ? data.urls : []))));
    const urls: string[] = rawList
      .map((item: any) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          return item.url || item.fileUrl || item.imageUrl || item.src || item.path || item.thumbnailUrl || '';
        }
        return '';
      })
      .filter((url: string) => Boolean(url && typeof url === 'string' && url.trim().length > 0));
    return { success: true, data: rawList, files: rawList, urls };
  } catch (err: any) {
    return { success: false, urls: [], message: err.message };
  }
};

export const getUserAvatarHistoryApi = async (
  folder: string = 'class/teacher_avatar'
): Promise<{ success: boolean; avatars: string[] }> => {
  try {
    const mediaRes = await getMediaListApi(folder);
    if (mediaRes.success && mediaRes.urls.length > 0) {
      return { success: true, avatars: mediaRes.urls };
    }
  } catch (e) {}

  try {
    const res = await apiClient.get(`/users/avatar-history`);
    return { success: true, avatars: res.data.avatars || res.data.data?.avatars || [] };
  } catch (err) {
    return { success: false, avatars: [] };
  }
};

export const updateUserAvatarApi = async (payload: { imageBase64?: string; avatar?: string; photoURL?: string; folder?: string }): Promise<{ success: boolean; message?: string; photoURL?: string; user?: UserProfile; avatars?: string[] }> => {
  try {
    const targetAvatar = payload.photoURL || payload.avatar || payload.imageBase64;
    // Backend đã loại bỏ /api/users/avatar, chuyển sang lưu vào CSDL qua /api/users/profile
    const res = await apiClient.put(`/users/profile`, {
      photoURL: targetAvatar,
      avatar: targetAvatar
    });
    const data = res.data;
    const rawUser = data.user || data.data?.user || data;
    const photoURL = data.photoURL || rawUser?.photoURL || rawUser?.avatar || targetAvatar;
    return {
      success: true,
      message: data.message || 'Cập nhật ảnh đại diện thành công!',
      photoURL,
      avatars: data.avatars || data.data?.avatars || (rawUser?.avatarHistory || []),
      user: rawUser ? normalizeUserProfile(rawUser) : undefined
    };
  } catch (err: any) {
    return { success: false, message: err.response?.data?.message || err.message };
  }
};

export const uploadMediaApi = async (
  file: File | Blob,
  folder: string = 'class/teacher_avatar',
  fileName?: string
): Promise<{ success: boolean; url?: string; message?: string; folder?: string; data?: any }> => {
  const formData = new FormData();
  if (file instanceof File) {
    formData.append('file', file);
  } else {
    formData.append('file', file, fileName || `avatar_${Date.now()}.jpg`);
  }
  formData.append('folder', folder);

  try {
    const res = await apiClient.post(`/v1/media/upload?folder=${encodeURIComponent(folder)}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    const data = res.data;
    const url = data.url || data.fileUrl || data.data?.url || data.data?.fileUrl;
    return {
      success: true,
      url,
      data,
      folder: data.folder || folder,
      message: data.message || 'Upload file thành công!'
    };
  } catch (err: any) {
    return { success: false, message: err.message };
  }
};

export const deleteGeminiApiKeyApi = async (): Promise<{ success: boolean; message: string; user: UserProfile }> => {
  const res = await apiClient.delete(`/user/gemini-key`);
  return {
    success: true,
    message: res.data.message || 'Đã xóa Gemini API Key thành công!',
    user: normalizeUserProfile(res.data.user)
  };
};

export const getHomeConfigApi = async (configKey: string = 'general_settings'): Promise<{
  success: boolean;
  configKey?: string;
  data?: any;
  header?: { url?: string };
  url?: string;
  faviconUrl?: string;
  googleAnalyticsId?: string;
}> => {
  try {
    const res = await apiClient.get(`/home_configs/${configKey}`);
    const data = res.data;
    const doc = data.data || data.config || data;
    const headerObj = doc?.header || data.header;
    const logoUrl = headerObj?.url || doc?.header?.url || doc?.header?.logoUrl;
    const faviconUrl = doc?.faviconUrl || data?.faviconUrl || doc?.data?.faviconUrl || data?.data?.faviconUrl;
    const googleAnalyticsId = doc?.googleAnalyticsId || data?.googleAnalyticsId || doc?.gtm_id || data?.gtm_id;

    if (faviconUrl && typeof document !== 'undefined') {
      let linkElements = document.querySelectorAll("link[rel*='icon']");
      if (linkElements.length > 0) {
        linkElements.forEach((linkEl) => {
          (linkEl as HTMLLinkElement).href = faviconUrl;
        });
      } else {
        const newLink = document.createElement('link');
        newLink.rel = 'shortcut icon';
        newLink.href = faviconUrl;
        document.getElementsByTagName('head')[0].appendChild(newLink);
      }
    }

    return {
      success: true,
      configKey,
      data: doc,
      header: headerObj || { url: logoUrl },
      url: logoUrl,
      faviconUrl: faviconUrl,
      googleAnalyticsId: typeof googleAnalyticsId === 'string' ? googleAnalyticsId : undefined
    };
  } catch (err) {
    return { success: false };
  }
};

export const getLibraryWalletApi = async (): Promise<{ success: boolean; credits: number; wallet?: any }> => {
  try {
    const res = await apiClient.get(`/library/wallet`);
    const data = res.data;
    const credits = typeof data.credits === 'number' ? data.credits : (data.wallet?.credits ?? 0);
    return { success: true, credits, wallet: data.wallet };
  } catch (err) {
    return { success: false, credits: 0 };
  }
};

export const getUserTransactionsApi = async (page: number = 1, limit: number = 10) => {
  try {
    const res = await apiClient.get(`/users/transactions?page=${page}&limit=${limit}`);
    const data = res.data;
    const rawList = data.data?.transactions || data.transactions || (Array.isArray(data.data) ? data.data : []);
    const totalCount = data.data?.pagination?.totalCount ?? data.pagination?.totalCount ?? data.totalCount ?? rawList.length;
    const totalPages = data.data?.pagination?.totalPages ?? data.pagination?.totalPages ?? (Math.ceil(totalCount / limit) || 1);
    return {
      success: true,
      transactions: rawList,
      pagination: { page, limit, totalCount, totalPages }
    };
  } catch (err) {
    return { success: false, transactions: [], pagination: { page, limit, totalCount: 0, totalPages: 1 } };
  }
};
